// Page super-admin « Trace Debug » — flux WS live d'une session relais.
//
// Réservée au rôle super_admin (gate au relais ET route protégée). Ouvre un WS
// direct vers le relais (jwt.<token> via /api/ws-token), démarre une trace sur
// une sessionCode et affiche le flux d'enveloppes en direct. Cf.
// a-faire/trace-debug-superadmin.md. Le même protocole est exposé en CLI
// (`remotews-cli trace …`) pour l'accès programmatique.

import { createSignal, For, Show, onCleanup } from "solid-js";
import { refreshRuntimeConfig } from "../shared/runtime-config";

interface TraceEvent {
  seq: number;
  t: number;
  dir: string;
  peerId?: string | null;
  peerRole?: string | null;
  family?: string | null;
  type?: string | null;
  op?: string | null;
  tool?: string | null;
  requestId?: string | null;
  bytes: number;
  routing?: string | null;
  note?: string | null;
  body?: unknown;
}

interface RelayWindow {
  __remotews_relay_ws_url__?: string;
  __remotews_relay_ws_urls__?: string[];
}

/** URL WS du relais : override `/config.json` (chargé au boot) sinon origine. */
function relayWsUrl(): string {
  const w = window as unknown as RelayWindow;
  const base = w.__remotews_relay_ws_url__ || (w.__remotews_relay_ws_urls__ && w.__remotews_relay_ws_urls__[0]);
  if (base) {
    return base.endsWith("/ws") ? base : `${base.replace(/\/$/, "")}/ws`;
  }
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

const DIR_COLOR: Record<string, string> = {
  in: "#3b82f6",
  out_local: "#22c55e",
  out_mesh: "#14b8a6",
  drop: "#ef4444",
};

export function SuperAdminTracePage() {
  const [sessionCode, setSessionCode] = createSignal("");
  const [bodies, setBodies] = createSignal(false);
  const [filterType, setFilterType] = createSignal("");
  const [status, setStatus] = createSignal("idle");
  const [active, setActive] = createSignal(false);
  const [events, setEvents] = createSignal<TraceEvent[]>([]);
  let ws: WebSocket | null = null;

  async function start() {
    const code = sessionCode().trim();
    if (!code) {
      setStatus("sessionCode requis");
      return;
    }
    closeWs();
    setEvents([]);
    setStatus("connexion…");
    let token: string | null = null;
    try {
      await refreshRuntimeConfig();
      const r = await fetch("/api/ws-token", { credentials: "include" });
      token = r.ok ? ((await r.json()) as { token?: string }).token || null : null;
    } catch {
      token = null;
    }
    if (!token) {
      setStatus("token WS indisponible (session expirée ?)");
      return;
    }
    ws = new WebSocket(relayWsUrl(), [`jwt.${token}`]);
    ws.addEventListener("open", () => {
      const ft = filterType().trim();
      const filter = ft ? { type: ft } : undefined;
      ws?.send(JSON.stringify({ type: "session:trace:start", sessionCode: code, bodies: bodies(), filter }));
    });
    ws.addEventListener("message", (e) => {
      let m: { type?: string; event?: TraceEvent; bodies?: boolean; code?: string; message?: string };
      try {
        m = JSON.parse(typeof e.data === "string" ? e.data : "");
      } catch {
        return;
      }
      if (m.type === "session:trace:event" && m.event) {
        setEvents((prev) => [m.event as TraceEvent, ...prev].slice(0, 2000));
      } else if (m.type === "session:trace:started") {
        setActive(true);
        setStatus(`trace active (bodies=${m.bodies})`);
      } else if (m.type === "error") {
        setStatus(`erreur: ${m.code || ""} ${m.message || ""}`);
      }
    });
    ws.addEventListener("close", () => {
      setActive(false);
      setStatus("déconnecté");
    });
    ws.addEventListener("error", () => setStatus("erreur WS"));
  }

  function closeWs() {
    if (ws) {
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "session:trace:stop", sessionCode: sessionCode().trim() }));
        }
      } catch {
        /* noop */
      }
      try {
        ws.close();
      } catch {
        /* noop */
      }
      ws = null;
    }
  }

  function stop() {
    closeWs();
    setActive(false);
    setStatus("arrêté");
  }

  function exportJson() {
    const blob = new Blob([JSON.stringify(events(), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trace-${sessionCode() || "session"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  onCleanup(() => closeWs());

  return (
    <div style={{ padding: "16px", "font-family": "monospace" }}>
      <h2 style={{ margin: "0 0 4px" }}>Trace Debug</h2>
      <div style={{ "font-size": "12px", opacity: 0.7, "margin-bottom": "12px" }}>
        « Wireshark applicatif » du relais — flux WS live d'une session. Réservé super_admin. Rédaction des secrets,
        time-box 10 min, zéro disque. Équivalent CLI : <code>remotews-cli trace watch --host &lt;id&gt;</code>.
      </div>

      <div
        style={{ display: "flex", gap: "8px", "align-items": "center", "flex-wrap": "wrap", "margin-bottom": "8px" }}
      >
        <input
          placeholder="sessionCode (ex. DXNYVBTW9PR93)"
          value={sessionCode()}
          onInput={(e) => setSessionCode(e.currentTarget.value)}
          style={{ padding: "6px 8px", width: "220px" }}
        />
        <input
          placeholder="filtre type (optionnel)"
          value={filterType()}
          onInput={(e) => setFilterType(e.currentTarget.value)}
          style={{ padding: "6px 8px", width: "180px" }}
        />
        <label style={{ "font-size": "12px" }}>
          <input type="checkbox" checked={bodies()} onChange={(e) => setBodies(e.currentTarget.checked)} /> bodies
          (rédigés)
        </label>
        <Show
          when={active()}
          fallback={
            <button type="button" onClick={() => void start()} style={{ padding: "6px 12px" }}>
              ▶ Démarrer
            </button>
          }
        >
          <button type="button" onClick={stop} style={{ padding: "6px 12px" }}>
            ■ Arrêter
          </button>
        </Show>
        <button type="button" onClick={exportJson} disabled={events().length === 0} style={{ padding: "6px 12px" }}>
          ⇩ Export
        </button>
        <span style={{ "font-size": "12px", opacity: 0.8 }}>
          {status()} · {events().length} events
        </span>
      </div>

      <table style={{ width: "100%", "font-size": "11px", "border-collapse": "collapse" }}>
        <thead>
          <tr style={{ "text-align": "left", "border-bottom": "1px solid #444" }}>
            <th>seq</th>
            <th>dir</th>
            <th>peer</th>
            <th>type/op</th>
            <th>tool</th>
            <th>requestId</th>
            <th>bytes</th>
            <th>routing</th>
            <th>note</th>
          </tr>
        </thead>
        <tbody>
          <For each={events()}>
            {(ev) => (
              <tr style={{ "border-bottom": "1px solid #2a2a2a" }}>
                <td>{ev.seq}</td>
                <td style={{ color: DIR_COLOR[ev.dir] || "#999", "font-weight": "bold" }}>{ev.dir}</td>
                <td>{ev.peerRole || ev.peerId || "—"}</td>
                <td>
                  {ev.type || "—"}
                  {ev.op ? `/${ev.op}` : ""}
                </td>
                <td>{ev.tool || "—"}</td>
                <td>{ev.requestId || "—"}</td>
                <td>{ev.bytes}</td>
                <td>{ev.routing || "—"}</td>
                <td>{ev.note || "—"}</td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  );
}
